/**
 * Memory gameplay module.
 * Exports: renderMemory(modal, data, onComplete), teardownMemory(modal)
 *
 * Cards are delivered with opaque per-session tokens only — no pairId or
 * image URL in the initial DOM. Image content + a per-pair group key are
 * fetched from /reveal only after a click, so inspecting the source cannot
 * reveal pairs. Attempts are submitted as token pairs; server resolves.
 */

import imgMemoryIcon from '../../images/memory-icon.svg';
import { renderGameMessage, startBlockedMessage } from '../shared/game-message';

export const WELCOME_CONFIG = {
    icon: imgMemoryIcon,
    title: 'Witaj w Beauty Match! 🧠✨',
    description: '<p>Przygotuj się na grę, która sprawdzi Twoją pamięć i koncentrację!</p>' +
        '<p>Twoim zadaniem jest odkrywanie kart, zapamiętywanie ich położenia i odnajdywanie pasujących par.</p>' +
        '<p>Uwaga – gra ma 2 etapy. W pierwszym znajdziesz 3 pary, w drugim aż 6. Każdy etap jest trudniejszy. Czas leci, więc liczy się skupienie.</p>',
    howToPlay: {
        heading: 'JAK GRAĆ?',
        body: '<ol><li>Odkrywaj karty, aby znaleźć pary.</li><li>Zapamiętuj, gdzie leżą poszczególne karty.</li><li>Działaj szybko – im więcej par znajdziesz, tym większa szansa na rekord!</li></ol><p>Gotowy? Sprawdź, ile par uda Ci się odkryć! 🔥✨</p>',
    },
    scoring: {
        heading: 'PUNKTACJA',
        body: '<ul>' +
            '<li>✅ +5 punkty za parę w etapie 1</li>' +
            '<li>✅ +5 punktów za parę w etapie 2</li>' +
            '<li>✅ +2 punkty bonusu za ukończenie etapu 1</li>' +
            '<li>✅ +3 punkty bonusu za ukończenie etapu 2</li>' +
            '<li>❌ -2 punkty za błędne dopasowanie (wynik nie spada poniżej 0)</li>' +
            '</ul>' +
            '<p>🏆 Maksymalny wynik to 50 punktów.</p>',
    },
};

let state = null;
let onCompleteCb = null;
let pendingTimeouts = [];
let timerHandle = null;
let timerStartTs = null;
let currentModal = null;

function closeIconSvg(_unused) {
    return `<svg viewBox="0 0 100 97" xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true"><path d="M62.704 48L77.3789 33.3251C80.8737 29.8303 80.8737 24.116 77.3789 20.6211C73.8841 17.1263 68.1697 17.1263 64.6749 20.6211L50 35.296L35.3251 20.6211C31.8303 17.1263 26.1159 17.1263 22.6211 20.6211C19.1263 24.1159 19.1263 29.8302 22.6211 33.3251L37.2962 48L22.6211 62.675C19.1263 66.1698 19.1263 71.8841 22.6211 75.3789C26.1159 78.8737 31.8303 78.8737 35.3251 75.3789L50 60.704L64.6749 75.3789C68.1697 78.8737 73.8841 78.8737 77.3789 75.3789C80.8737 71.8841 80.8737 66.1698 77.3789 62.675L62.704 48Z" fill="currentColor"/></svg>`;
}

function schedule(fn, ms) {
    const id = setTimeout(() => {
        pendingTimeouts = pendingTimeouts.filter(t => t !== id);
        fn();
    }, ms);
    pendingTimeouts.push(id);
    return id;
}

function clearPending() {
    pendingTimeouts.forEach(clearTimeout);
    pendingTimeouts = [];
}

function content(modal) {
    return modal.querySelector('.game-modal__content');
}

function colsForCardCount(count) {
    if (count <= 6)  return 2;
    if (count <= 12) return 3;
    return 4;
}

function rowsForCardCount(count, cols) {
    return Math.ceil(count / cols);
}

function startTimer(modal) {
    timerStartTs = Date.now();
    if (timerHandle) clearInterval(timerHandle);
    const tick = () => {
        const el = modal.querySelector('#js-memory-timer');
        if (!el) return;
        const elapsed = Math.floor((Date.now() - timerStartTs) / 1000);
        const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
        const ss = String(elapsed % 60).padStart(2, '0');
        el.textContent = `${mm}:${ss}`;
    };
    tick();
    timerHandle = setInterval(tick, 1000);
}

function stopTimer() {
    if (timerHandle) {
        clearInterval(timerHandle);
        timerHandle = null;
    }
}

function createState(data) {
    return {
        taskId:                 data.task.id,
        sessionToken:           data.sessionToken,
        gameName:               data.memoryGame.name,
        config:                 data.memoryGame.config,
        rounds:                 data.memoryGame.rounds,
        currentRoundIdx:        0,
        lastCompletedRoundIdx:  -1,
        attemptsByRound:        data.memoryGame.rounds.map(() => []),
        matchedInCurrentRound:  new Set(),
        flipped:                [],
        locked:                 false,
        revealCache:            new Map(),
    };
}

// ── Screens ───────────────────────────────────────────────────────────────────

function renderRound(modal) {
    const round = state.rounds[state.currentRoundIdx];
    state.matchedInCurrentRound = new Set();
    state.flipped = [];
    state.locked = false;

    const roundNum  = state.currentRoundIdx + 1;
    const total     = state.rounds.length;
    const cardCount = round.pairs.length;
    const cols      = colsForCardCount(cardCount);
    const rows      = rowsForCardCount(cardCount, cols);

    const heading = round.question
        ? escHtml(round.question)
        : 'Znajdź takie same pary obrazków<br>w określonym czasie';

    const cardsHtml = round.pairs.map((card, idx) => `
        <div class="s-memory__card js-memory-card"
             data-card-idx="${idx}"
             data-token="${escHtml(card.token)}">
            <div class="s-memory__card-inner">
                <div class="s-memory__card-back"></div>
                <div class="s-memory__card-front">
                    <img alt="">
                </div>
            </div>
        </div>
    `).join('');

    content(modal).innerHTML = `
        <div class="game-modal__inner s-memory s-memory--round">
            <div class="s-memory__topbar">
                <div class="s-memory__level">
                    <span class="s-memory__level-label">POZIOM:</span>
                    <span class="s-memory__level-value">${roundNum}/${total}</span>
                </div>
                <button class="s-memory__close js-game-modal-close" type="button" aria-label="Zamknij">
                    ${closeIconSvg('#FFFFF0')}
                </button>
            </div>
            <div class="s-memory__timer" id="js-memory-timer">00:00</div>
            <h2 class="s-memory__heading">${heading}</h2>
            <div class="s-memory__board-wrap">
                <div class="s-memory__board" style="--cols: ${cols}; --rows: ${rows};">
                    ${cardsHtml}
                </div>
            </div>
        </div>
    `;

    document.body.classList.add('has-memory-round');
    startTimer(modal);
}

function renderBetween(modal) {
    stopTimer();

    const inner = modal.querySelector('.s-memory--round');
    if (!inner) {
        // Fallback: shouldn't happen, but if for some reason round screen
        // isn't there, render fresh round screen first.
        renderRound(modal);
        return renderBetween(modal);
    }

    const overlay = document.createElement('div');
    overlay.className = 's-memory__between-overlay';
    overlay.innerHTML = `
        <div class="s-memory__between-wrap">
            <div class="s-memory__between-card">
                <button class="s-memory__between-close js-memory-between-close" type="button" aria-label="Zamknij">
                    ${closeIconSvg('#FF5B84')}
                </button>
                <h2 class="s-memory__between-title">Idzie Ci świetnie!</h2>
                <p class="s-memory__between-sub">Przygotuj się na 2 razy więcej kart<br>do zapamiętania ;)</p>
                <img class="s-memory__between-icon" src="${imgMemoryIcon}" alt="">
            </div>
            <div class="s-memory__between-foot">
                <button class="s-memory__between-action js-memory-next-round" type="button">
                    Następny poziom
                </button>
            </div>
        </div>
    `;
    inner.appendChild(overlay);
}

function renderSending(modal) {
    stopTimer();
    content(modal).innerHTML = `
        <div class="game-modal__inner s-memory s-memory--sending">
            <p class="s-memory__loading">Wysyłanie wyniku…</p>
        </div>
    `;
}

function renderResult(modal) {
    // Result UI is the shared `.game-result` panel toggled by
    // `game-modal:result-show` event — we just clear our own content.
    content(modal).innerHTML = '';
}

function renderError(modal, msg) {
    stopTimer();
    content(modal).innerHTML = `
        <div class="game-modal__inner s-memory s-memory--error">
            <div>
                <p class="s-memory__error">${escHtml(msg)}</p>
                <button class="s-memory__error-btn js-game-close-results" type="button">Zamknij</button>
            </div>
        </div>
    `;
}

// ── Gameplay ──────────────────────────────────────────────────────────────────

async function onCardClick(modal, cardEl) {
    if (state.locked) return;
    if (cardEl.classList.contains('is-flipped')) return;
    if (cardEl.classList.contains('is-matched')) return;
    if (state.flipped.length >= 2) return;

    const token = cardEl.dataset.token;

    cardEl.classList.add('is-flipped');
    state.locked = true;

    let revealed = state.revealCache.get(token);
    if (!revealed) {
        const elapsedMs = timerStartTs ? Math.floor(Date.now() - timerStartTs) : 0;
        try {
            const res = await fetch(`/api/task/${state.taskId}/memory/reveal`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ token, sessionToken: state.sessionToken, elapsedMs }),
            });
            if (!res.ok) throw new Error('reveal_failed');
            revealed = await res.json();
        } catch {
            if (!state) return;
            cardEl.classList.remove('is-flipped');
            state.locked = false;
            return;
        }

        if (!state) return;
        state.revealCache.set(token, revealed);
    }

    const img = cardEl.querySelector('.s-memory__card-front img');
    if (img) img.src = revealed.content;

    state.flipped.push({
        el:        cardEl,
        token,
        pairGroup: revealed.pairGroup,
        idx:       cardEl.dataset.cardIdx,
    });

    if (state.flipped.length < 2) {
        state.locked = false;
        return;
    }

    const [a, b] = state.flipped;
    state.attemptsByRound[state.currentRoundIdx].push([a.token, b.token]);
    schedule(() => resolvePair(modal, a, b), 500);
}

function resolvePair(modal, a, b) {
    if (!state) return;
    if (a.pairGroup === b.pairGroup && a.token !== b.token) {
        a.el.classList.add('is-matched');
        b.el.classList.add('is-matched');
        state.matchedInCurrentRound.add(a.pairGroup);

        const round = state.rounds[state.currentRoundIdx];
        const pairCount = round.pairs.length / 2;

        state.flipped = [];
        state.locked = false;

        if (state.matchedInCurrentRound.size === pairCount) {
            schedule(() => onRoundComplete(modal), 600);
        }
    } else {
        a.el.classList.add('is-wrong');
        b.el.classList.add('is-wrong');
        schedule(() => {
            a.el.classList.remove('is-flipped', 'is-wrong');
            b.el.classList.remove('is-flipped', 'is-wrong');
            if (!state) return;
            const aImg = a.el.querySelector('.s-memory__card-front img');
            const bImg = b.el.querySelector('.s-memory__card-front img');
            if (aImg) aImg.removeAttribute('src');
            if (bImg) bImg.removeAttribute('src');
            state.flipped = [];
            state.locked = false;
        }, 500);
    }
}

async function flushLevel(taskId) {
    const round     = state.rounds[state.currentRoundIdx];
    const elapsedMs = timerStartTs ? Math.floor(Date.now() - timerStartTs) : 0;
    try {
        await fetch(`/api/task/${taskId}/memory/level`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ sessionToken: state.sessionToken, round: round.round, elapsedMs }),
        });
    } catch {
        // non-critical — game continues regardless
    }
}

async function onRoundComplete(modal) {
    if (!state) return;
    await flushLevel(state.taskId);
    state.lastCompletedRoundIdx = state.currentRoundIdx;
    if (state.currentRoundIdx < state.rounds.length - 1) {
        renderBetween(modal);
    } else {
        submitGame(modal);
    }
}

async function submitGame(modal) {
    renderSending(modal);

    const payload = {
        sessionToken: state.sessionToken,
        rounds: state.rounds.map((r, i) => ({
            round:    r.round,
            attempts: state.attemptsByRound[i],
        })),
    };

    try {
        const res = await fetch(`/api/task/${state.taskId}/memory/submit`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(payload),
        });
        const data = await res.json();

        if (res.ok) {
            renderResult(modal);
            if (onCompleteCb) onCompleteCb(data);
            document.dispatchEvent(new CustomEvent('game-modal:result-show', {
                detail: {
                    rankingPoints: data.totalRankingPoints ?? 0,
                    redeemablePoints: data.totalRedeemablePoints ?? 0,
                },
            }));
        } else if (res.status === 409 && data?.error === 'already_completed') {
            renderError(modal, 'To zadanie zostało już zaliczone.');
            if (onCompleteCb) onCompleteCb();
        } else if (res.status === 409 && data?.error === 'session_expired') {
            renderError(modal, 'Sesja gry wygasła. Odśwież stronę i zagraj ponownie.');
        } else {
            renderError(modal, 'Błąd zapisu wyniku. Odśwież stronę i spróbuj ponownie.');
        }
    } catch {
        renderError(modal, 'Błąd połączenia. Spróbuj ponownie.');
    }
}

// ── Event delegation ──────────────────────────────────────────────────────────

function onModalClick(e) {
    const modal = e.currentTarget;

    if (e.target.closest('.js-memory-next-round')) {
        const overlay = modal.querySelector('.s-memory__between-overlay');
        if (overlay) overlay.remove();
        state.currentRoundIdx++;
        renderRound(modal);
        return;
    }

    if (e.target.closest('.js-memory-between-close')) {
        const overlay = modal.querySelector('.s-memory__between-overlay');
        if (overlay) overlay.remove();
        return;
    }

    const cardEl = e.target.closest('.js-memory-card');
    if (cardEl) {
        onCardClick(modal, cardEl);
        return;
    }
}

// ── Utility ───────────────────────────────────────────────────────────────────

function escHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function renderMemory(modal, data, onComplete) {
    onCompleteCb = onComplete;

    content(modal).innerHTML = `
        <div class="game-modal__inner s-memory s-memory--loading">
            <p class="s-memory__loading">Ładowanie…</p>
        </div>
    `;
    document.body.classList.add('has-memory-round');

    let startData;
    try {
        const res = await fetch(`/api/task/${data.task.id}/memory/start`, {
            method:      'POST',
            headers:     { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
        });
        if (res.status === 409) {
            const body = await res.json().catch(() => ({}));
            renderGameMessage(modal, startBlockedMessage(body?.error));
            return;
        }
        if (!res.ok) {
            renderError(modal, 'Błąd rozpoczęcia gry.');
            return;
        }
        startData = await res.json();
    } catch {
        renderError(modal, 'Błąd połączenia.');
        return;
    }

    state = createState({
        task:         data.task,
        sessionToken: startData.sessionToken,
        memoryGame:   { ...data.memoryGame, rounds: startData.rounds },
    });
    state.currentRoundIdx = 0;
    currentModal = modal;
    renderRound(modal);
    modal.addEventListener('click', onModalClick);
}

export async function forceEndMemory() {
    if (!state) return;

    const modal                = currentModal;
    const taskId               = state.taskId;
    const sessionToken         = state.sessionToken;
    const lastCompletedRoundIdx = state.lastCompletedRoundIdx;
    const rounds               = state.rounds;
    const attemptsByRound      = state.attemptsByRound;
    const cb                   = onCompleteCb;

    // Build payload for completed rounds before clearing state.
    const completedRounds = [];
    for (let i = 0; i <= lastCompletedRoundIdx; i++) {
        completedRounds.push({
            round:    rounds[i].round,
            attempts: attemptsByRound[i],
        });
    }

    clearPending();
    stopTimer();
    document.body.classList.remove('has-memory-round');
    if (modal) modal.removeEventListener('click', onModalClick);
    state        = null;
    onCompleteCb = null;
    currentModal = null;

    if (modal) {
        const el = modal.querySelector('.game-modal__content');
        if (el) el.innerHTML = '<div class="game-modal__inner s-memory s-memory--sending"><p class="s-memory__loading">Wysyłanie wyniku…</p></div>';
    }

    let data = null;
    try {
        const res = await fetch(`/api/task/${taskId}/memory/abandon`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ sessionToken, rounds: completedRounds }),
        });
        data = await res.json();
    } catch {
        data = { totalRankingPoints: 0, totalRedeemablePoints: 0 };
    }

    if (cb) cb(data);
    document.dispatchEvent(new CustomEvent('game-modal:result-show', {
        detail: {
            rankingPoints:    data.totalRankingPoints    ?? 0,
            redeemablePoints: data.totalRedeemablePoints ?? 0,
        },
    }));
}

export function teardownMemory(modal) {
    modal.removeEventListener('click', onModalClick);
    clearPending();
    stopTimer();
    document.body.classList.remove('has-memory-round');
    state        = null;
    onCompleteCb = null;
    currentModal = null;
}
